import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';
import { FilteringTag, SearchResult } from 'jbc-front/components/SearchForm';
import { yup } from '@/libs/yup';
import { MainFormControls } from '@/components/SearchForm/MainFormControls';
import { SearchTextBox } from '@/components/SearchForm/SearchTextBox';
import { SortingFields } from '@/components/SearchForm/SortingFields';
import { useDisplayEmployeeLimit } from '@/components/utils/display_employee_limit';
import { useDebouncedSubmit } from '@/hooks/useDebouncedSubmit';
import { DownloadModal } from '../DownloadModal';
import { AdditionalSearchField } from './AdditionalSearchField';
import styles from './SearchForm.scss';

const IGNORE_AUTO_SUBMIT_FIELDS = ['status', 'limit', 'sortType'];

const limits = ['10', '20', '50', '100'].map(ele => ({ value: ele, label: `${ele}件を表示` }));

const sortTypes = [
  { value: 'staff_code__asc', label: 'スタッフコード 昇順' },
  { value: 'staff_code__desc', label: 'スタッフコード 降順' },
  { value: 'full_name_kana__asc', label: '氏名 昇順' },
  { value: 'full_name_kana__desc', label: '氏名 降順' },
  { value: 'joined_at__desc', label: '入社日 新しい順' },
  { value: 'joined_at__asc', label: '入社日 古い順' }
];

const employmentStatuses = [
  { id: 'all', name: 'すべて' },
  { id: 'employed', name: '在職中' },
  { id: 'vacation', name: '休職中' },
  { id: 'unemployed', name: '退職済' },
  { id: 'join', name: '内定' }
] as const;

type EmploymentStatus = (typeof employmentStatuses)[number]['id'];

const mainFieldSchema = yup.object({
  q: yup.string(),
  status: yup
    .string()
    .oneOf(employmentStatuses.map(item => item.id))
    .required()
});

const additionalFieldSchema = yup.object({
  offices: yup.array(yup.string().required()),
  groups: yup.array(yup.string().required()),
  positions: yup.array(yup.string().required()),
  employmentTypes: yup.array(yup.string().required())
});

const sortTypeSchema = yup.object({
  sortType: yup
    .string()
    .oneOf(sortTypes.map(item => item.value))
    .required()
});

const paginatorSchema = yup.object({
  page: yup.string(),
  limit: yup.string()
});

const schema = mainFieldSchema.concat(additionalFieldSchema).concat(sortTypeSchema).concat(paginatorSchema);

export type Schema = yup.InferType<typeof schema>;

interface SearchFormProps {
  values: Schema;
  totalCount?: number;
  isLoading: boolean;
  onSubmit: (data: Schema) => void;
  onClear: () => void;
  hasConditions: (data: Schema) => boolean;
}

export const SearchForm: FC<SearchFormProps> = ({
  values,
  totalCount,
  isLoading,
  onSubmit,
  onClear,
  hasConditions
}) => {
  const [isExpand, setIsExpand] = useState(false);
  const [limit, setLimit] = useDisplayEmployeeLimit();
  const methods = useForm<Schema>({
    values,
    resolver: yupResolver(schema)
  });
  const { handleSubmit, register, watch, setValue, subscribe } = methods;

  const formValues = watch();
  const { status, page } = formValues;

  useDebouncedSubmit(subscribe, () => handleSubmit(onSubmit)(), {
    ignoreAutoSubmitFields: IGNORE_AUTO_SUBMIT_FIELDS
  });

  return (
    <FormProvider {...methods}>
      <div className={styles.searchForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.mainSearchFields}>
            <EmploymentStatusField
              value={status}
              onChange={value => {
                if (status !== value) {
                  setValue('status', value);
                  handleSubmit(onSubmit)();
                }
              }}
            />
            <MainFormControls
              isExpand={isExpand}
              hasConditions={hasConditions(formValues)}
              onClear={onClear}
              onToggleClick={() => setIsExpand(prev => !prev)}>
              <SearchTextBox
                {...register('q')}
                style={{ width: 230 }}
                placeholder="スタッフコード、氏名、メールアドレス"
              />
            </MainFormControls>
          </div>
          {isExpand && <AdditionalSearchField />}
          <div className={styles.result}>
            <div className={styles.resultButtonContainer}>
              <DownloadModal />
              <Button as={Link} to="/regular_health_checkup/personal_results/import" widthAuto primary>
                個人結果登録・更新・削除
              </Button>
            </div>
            <SearchResult
              className={styles.resultCenter}
              total={totalCount}
              page={page}
              limit={limit}
              isLoading={isLoading}
              failureMessage=""
            />
            <SortingFields
              limits={limits}
              onChangeLimit={newValue => {
                setLimit(newValue);
                handleSubmit(onSubmit)();
              }}
              sortTypes={sortTypes}
              onChangeSortType={() => handleSubmit(onSubmit)()}
            />
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

interface EmploymentStatusFieldProps {
  value: EmploymentStatus;
  onChange: (value: EmploymentStatus) => void;
}

const EmploymentStatusField: FC<EmploymentStatusFieldProps> = ({ value, onChange }) => {
  return (
    <div className={styles.employmentStatusField}>
      {employmentStatuses.map(item => (
        <FilteringTag key={item.id} selected={item.id === value} onClick={() => onChange(item.id)}>
          <div className={styles.employmentStatus}>{item.name}</div>
        </FilteringTag>
      ))}
    </div>
  );
};
